/**
 * App Lock — a local PIN gate over the persisted Supabase session.
 *
 * The PIN is hashed with PBKDF2-SHA256 (per-user salt, high iteration count).
 * The hash lives in `user_app_pins` (owner-only RLS) as the cross-device source
 * of truth AND is cached in localStorage so unlock works INSTANTLY and OFFLINE
 * (the Canvas app is used in the field). The non-secret flags `pin_enabled` /
 * `pin_length` live on `profiles` so the app can decide to show the lock without
 * a round-trip.
 *
 * Threat model: a deterrent against someone grabbing an unlocked phone — not a
 * guarantee against an attacker who already has the device + saved session.
 */
import { supabase } from '@/integrations/supabase/client';

// `user_app_pins` + the new profiles PIN columns aren't in the generated types
// yet, so reach them through an untyped handle.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const ITERATIONS = 150_000;
const cacheKey = (userId: string) => `nk-applock-${userId}`;
// Cold-start hint shared with PinLockOverlay — lets a fresh launch cover the
// screen instantly (before the async resolve) without flashing the app.
const HINT_KEY = 'nk-applock-on';
function setHint(on: boolean) {
    try { localStorage.setItem(HINT_KEY, on ? '1' : '0'); } catch { /* noop */ }
}

interface PinCache {
    hash: string;
    salt: string;
    iterations: number;
    length: number;
}

/** When the PIN is asked for. Defaults mirror the original hardcoded behavior. */
export interface PinSettings {
    lockOnLaunch: boolean;        // lock on a cold app start / reopen
    lockOnResume: boolean;        // lock when returning from the background…
    resumeAfterSeconds: number;   // …after being hidden at least this long
    lockOnIdle: boolean;          // lock after sitting idle in-app
    idleMinutes: number;          // …this many minutes of no interaction
}

export const DEFAULT_PIN_SETTINGS: PinSettings = {
    lockOnLaunch: true,
    lockOnResume: true,
    resumeAfterSeconds: 30,
    lockOnIdle: false,
    idleMinutes: 5,
};

export interface PinState extends PinSettings {
    enabled: boolean;
    length: number;
}

function toHex(buf: ArrayBuffer): string {
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function randomSalt(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function hashPin(pin: string, salt: string, iterations: number): Promise<string> {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
    const bits = await crypto.subtle.deriveBits(
        { name: 'PBKDF2', salt: enc.encode(salt), iterations, hash: 'SHA-256' },
        keyMaterial,
        256,
    );
    return toHex(bits);
}

function readCache(userId: string): PinCache | null {
    try {
        const raw = localStorage.getItem(cacheKey(userId));
        return raw ? (JSON.parse(raw) as PinCache) : null;
    } catch { return null; }
}

function writeCache(userId: string, cache: PinCache) {
    try { localStorage.setItem(cacheKey(userId), JSON.stringify(cache)); } catch { /* private mode */ }
}

function clearCache(userId: string) {
    try { localStorage.removeItem(cacheKey(userId)); } catch { /* noop */ }
}

/** Pull the hash row + length and cache it (called on load when online). */
export async function refreshPinCache(userId: string): Promise<void> {
    const [{ data: pin }, { data: profile }] = await Promise.all([
        db.from('user_app_pins').select('pin_hash, pin_salt, iterations').eq('user_id', userId).maybeSingle(),
        db.from('profiles').select('pin_length').eq('id', userId).maybeSingle(),
    ]);
    const p = pin as { pin_hash?: string; pin_salt?: string; iterations?: number } | null;
    if (p?.pin_hash && p.pin_salt && p.iterations) {
        writeCache(userId, {
            hash: p.pin_hash,
            salt: p.pin_salt,
            iterations: p.iterations,
            length: (profile as { pin_length?: number } | null)?.pin_length ?? 4,
        });
    }
}

/** Whether the lock is enabled for this user + how many digits. Refreshes the
 *  local hash cache when online so a cross-device set is picked up. */
export async function loadPinState(userId: string): Promise<PinState> {
    const { data } = await db.from('profiles')
        .select('pin_enabled, pin_length, pin_lock_on_launch, pin_lock_on_resume, pin_resume_after_seconds, pin_lock_on_idle, pin_idle_minutes')
        .eq('id', userId).maybeSingle();
    const row = data as {
        pin_enabled?: boolean; pin_length?: number;
        pin_lock_on_launch?: boolean; pin_lock_on_resume?: boolean; pin_resume_after_seconds?: number;
        pin_lock_on_idle?: boolean; pin_idle_minutes?: number;
    } | null;
    const enabled = !!row?.pin_enabled;
    if (enabled && navigator.onLine) { try { await refreshPinCache(userId); } catch { /* offline-ish */ } }
    const cached = readCache(userId);
    return {
        enabled,
        length: row?.pin_length ?? cached?.length ?? 4,
        lockOnLaunch: row?.pin_lock_on_launch ?? DEFAULT_PIN_SETTINGS.lockOnLaunch,
        lockOnResume: row?.pin_lock_on_resume ?? DEFAULT_PIN_SETTINGS.lockOnResume,
        resumeAfterSeconds: row?.pin_resume_after_seconds ?? DEFAULT_PIN_SETTINGS.resumeAfterSeconds,
        lockOnIdle: row?.pin_lock_on_idle ?? DEFAULT_PIN_SETTINGS.lockOnIdle,
        idleMinutes: row?.pin_idle_minutes ?? DEFAULT_PIN_SETTINGS.idleMinutes,
    };
}

/** Persist the timing preferences. */
export async function savePinSettings(userId: string, s: PinSettings): Promise<void> {
    const { error } = await db.from('profiles').update({
        pin_lock_on_launch: s.lockOnLaunch,
        pin_lock_on_resume: s.lockOnResume,
        pin_resume_after_seconds: s.resumeAfterSeconds,
        pin_lock_on_idle: s.lockOnIdle,
        pin_idle_minutes: s.idleMinutes,
    }).eq('id', userId);
    if (error) throw new Error(error.message);
    try { localStorage.setItem(LAUNCH_HINT_KEY, s.lockOnLaunch ? '1' : '0'); } catch { /* noop */ }
}

/** Cold-start hint for "lock on launch" — lets the instant cover honor the pref. */
export const LAUNCH_HINT_KEY = 'nk-applock-launch';

export async function setPin(userId: string, pin: string, length: number): Promise<void> {
    const salt = randomSalt();
    const hash = await hashPin(pin, salt, ITERATIONS);
    const { error: upErr } = await db.from('user_app_pins').upsert({
        user_id: userId, pin_hash: hash, pin_salt: salt, iterations: ITERATIONS, updated_at: new Date().toISOString(),
    });
    if (upErr) throw new Error(upErr.message);
    const { error: pErr } = await db.from('profiles').update({ pin_enabled: true, pin_length: length }).eq('id', userId);
    if (pErr) throw new Error(pErr.message);
    writeCache(userId, { hash, salt, iterations: ITERATIONS, length });
    setHint(true);
    // Don't clobber an existing launch pref on a "Change PIN"; only seed it the
    // first time (default on).
    try { if (localStorage.getItem(LAUNCH_HINT_KEY) === null) localStorage.setItem(LAUNCH_HINT_KEY, '1'); } catch { /* noop */ }
}

export async function disablePin(userId: string): Promise<void> {
    await db.from('profiles').update({ pin_enabled: false }).eq('id', userId);
    await db.from('user_app_pins').delete().eq('user_id', userId);
    clearCache(userId);
    setHint(false);
    try { localStorage.removeItem(LAUNCH_HINT_KEY); } catch { /* noop */ }
}

/** Verify a PIN against the cached hash (offline-capable). Falls back to a
 *  one-time DB fetch if there's no cache yet but we're online. */
export async function verifyPin(userId: string, pin: string): Promise<boolean> {
    let cache = readCache(userId);
    if (!cache && navigator.onLine) {
        await refreshPinCache(userId);
        cache = readCache(userId);
    }
    if (!cache) return false;
    const candidate = await hashPin(pin, cache.salt, cache.iterations);
    return candidate === cache.hash;
}
